import React, { useState, useEffect } from 'react';
import axiosInstance from '../api/axiosConfig';
import { HiOutlinePlus, HiOutlineShieldCheck, HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { useAuth } from '../context/AuthContext';
import './RoleManagement.css';

const RoleManagement = () => {
    const { user } = useAuth();
    const [roles, setRoles] = useState([]);
    const [availableCards, setAvailableCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedRole, setSelectedRole] = useState(null);
    
    const [formData, setFormData] = useState({
        roleId: '',
        roleName: '',
        displayName: '',
        permissions: {
            cards: [],
            actions: ['view'],
            cardActions: {}
        }
    });

    useEffect(() => {
        fetchRoles();
        fetchAvailableCards();
    }, []);

    const fetchRoles = async () => {
        try {
            setLoading(true);
            const response = await axiosInstance.get('/api/roles');
            setRoles(response.data.roles);
        } catch (err) {
            console.error('Failed to fetch roles:', err);
            alert('Failed to fetch roles');
        } finally {
            setLoading(false);
        }
    };

    const fetchAvailableCards = async () => {
        try {
            const response = await axiosInstance.get('/api/roles/available-cards');
            setAvailableCards(response.data.cards);
        } catch (err) {
            console.error('Failed to fetch available cards:', err);
        }
    };

    const handleCreateRole = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.post('/api/roles', formData);
            setShowCreateModal(false);
            resetForm();
            fetchRoles();
            alert('Role created successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to create role');
        }
    };

    const handleUpdateRole = async (e) => {
        e.preventDefault();
        try {
            await axiosInstance.put(`/api/roles/${selectedRole._id}`, {
                roleName: formData.roleName,
                displayName: formData.displayName,
                permissions: formData.permissions
            });
            setShowEditModal(false);
            setSelectedRole(null);
            resetForm();
            fetchRoles();
            alert('Role updated successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to update role');
        }
    };

    const handleDeleteRole = async (roleId, isSystemRole) => {
        const confirmMessage = isSystemRole 
            ? 'Warning: This is a system role. Deleting it may affect system functionality. Are you sure?'
            : 'Are you sure you want to delete this role?';
            
        if (!window.confirm(confirmMessage)) return;
        
        try {
            await axiosInstance.delete(`/api/roles/${roleId}`);
            fetchRoles();
            alert('Role deleted successfully');
        } catch (err) {
            alert(err.response?.data?.message || 'Failed to delete role');
        }
    };

    const openEditModal = (role) => {
        setSelectedRole(role);
        setFormData({
            roleId: role.roleId,
            roleName: role.roleName,
            displayName: role.displayName,
            permissions: {
                cards: role.permissions?.cards || [],
                actions: role.permissions?.actions || [],
                cardActions: role.permissions?.cardActions || {}
            }
        });
        setShowEditModal(true);
    };

    const resetForm = () => {
        setFormData({
            roleId: '',
            roleName: '',
            displayName: '',
            permissions: {
                cards: [],
                actions: ['view'],
                cardActions: {}
            }
        });
    };

    const toggleCard = (cardId) => {
        setFormData(prev => {
            const newCards = prev.permissions.cards.includes(cardId)
                ? prev.permissions.cards.filter(c => c !== cardId)
                : [...prev.permissions.cards, cardId];
            
            // Remove card-specific actions if card is removed
            const newCardActions = { ...prev.permissions.cardActions };
            if (!newCards.includes(cardId)) {
                delete newCardActions[cardId];
            }
            
            return {
                ...prev,
                permissions: {
                    ...prev.permissions,
                    cards: newCards,
                    cardActions: newCardActions
                }
            };
        });
    };

    const toggleAction = (action) => {
        setFormData(prev => ({
            ...prev,
            permissions: {
                ...prev.permissions,
                actions: prev.permissions.actions.includes(action)
                    ? prev.permissions.actions.filter(a => a !== action)
                    : [...prev.permissions.actions, action]
            }
        }));
    };

    const toggleCardAction = (cardId, action) => {
        setFormData(prev => {
            const cardActions = prev.permissions.cardActions[cardId] || [];
            const newCardActions = cardActions.includes(action)
                ? cardActions.filter(a => a !== action)
                : [...cardActions, action];
            
            return {
                ...prev,
                permissions: {
                    ...prev.permissions,
                    cardActions: {
                        ...prev.permissions.cardActions,
                        [cardId]: newCardActions
                    }
                }
            };
        });
    };

    if (loading) {
        return <div className="loading">Loading roles...</div>;
    }

    return (
        <div className="role-management">
            {/* Card list styled by the um- rules in AdminDashboard.css, so this
                tab matches the rest of User Management; the modals are unchanged. */}
            <div className="um-section-head">
                <div>
                    <h2>Roles</h2>
                    <p>{roles.length} roles · a role is the default set of cards and actions for its users</p>
                </div>
                <button className="um-btn um-btn-primary" onClick={() => setShowCreateModal(true)}>
                    <HiOutlinePlus /> <span>Create Role</span>
                </button>
            </div>

            <div className="um-role-grid">
                {roles.map(role => {
                    const cards = role.permissions?.cards || [];
                    return (
                        <article key={role._id} className="um-role-card">
                            <header>
                                <span className="um-avatar"><HiOutlineShieldCheck /></span>
                                <div className="um-user-text">
                                    <div className="um-name">{role.displayName}</div>
                                    <div className="um-id">{role.roleId}</div>
                                </div>
                                {role.isSystemRole && <span className="um-chip um-system">System</span>}
                            </header>
                            <div className="um-role-cards">
                                <div className="um-sub">{cards.length} {cards.length === 1 ? 'card' : 'cards'}</div>
                                <div className="um-chip-row">
                                    {cards.slice(0, 4).map(card => (
                                        <span key={card} className="um-chip">{card}</span>
                                    ))}
                                    {cards.length > 4 && <span className="um-chip um-more">+{cards.length - 4} more</span>}
                                    {cards.length === 0 && <span className="um-none">No cards</span>}
                                </div>
                            </div>
                            <footer>
                                <button className="um-btn um-btn-sm" onClick={() => openEditModal(role)}>
                                    <HiOutlinePencil /> Edit
                                </button>
                                <button
                                    className="um-btn um-btn-sm um-btn-danger"
                                    onClick={() => handleDeleteRole(role._id, role.isSystemRole)}
                                >
                                    <HiOutlineTrash /> Delete
                                </button>
                            </footer>
                        </article>
                    );
                })}
            </div>

            {/* Create Role Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-content role-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Create New Role</h2>
                        <form onSubmit={handleCreateRole}>
                            <div className="form-group">
                                <label>Role ID *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.roleId}
                                    onChange={(e) => setFormData({...formData, roleId: e.target.value})}
                                    placeholder="e.g., finance_manager"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Role Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.roleName}
                                    onChange={(e) => setFormData({...formData, roleName: e.target.value})}
                                    placeholder="e.g., Finance Manager"
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Display Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.displayName}
                                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                                    placeholder="e.g., Finance Manager"
                                    required
                                />
                            </div>
                            
                            <div className="permission-section">
                                <h3>Global Actions (Fallback)</h3>
                                <p className="help-text">These actions apply to all cards unless card-specific actions are defined</p>
                                <div className="action-checkboxes">
                                    {['view', 'create', 'edit', 'delete', 'approve'].map(action => (
                                        <label key={action} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.permissions.actions.includes(action)}
                                                onChange={() => toggleAction(action)}
                                            />
                                            <span>{action}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="permission-section">
                                <h3>Card Access & Actions</h3>
                                <p className="help-text">Select cards and optionally define specific actions per card</p>
                                <div className="cards-list">
                                    {availableCards.map(card => (
                                        <div key={card.id} className="card-permission-item">
                                            <label className="card-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.cards.includes(card.id)}
                                                    onChange={() => toggleCard(card.id)}
                                                />
                                                <strong>{card.icon} {card.title}</strong>
                                            </label>
                                            
                                            {formData.permissions.cards.includes(card.id) && (
                                                <div className="card-actions">
                                                    <span className="card-actions-label">Specific actions:</span>
                                                    {['view', 'create', 'edit', 'delete', 'approve'].map(action => (
                                                        <label key={action} className="checkbox-label-small">
                                                            <input
                                                                type="checkbox"
                                                                checked={(formData.permissions.cardActions[card.id] || []).includes(action)}
                                                                onChange={() => toggleCardAction(card.id, action)}
                                                            />
                                                            {action}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowCreateModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Create Role
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Role Modal */}
            {showEditModal && (
                <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
                    <div className="modal-content role-modal" onClick={(e) => e.stopPropagation()}>
                        <h2>Edit Role</h2>
                        <form onSubmit={handleUpdateRole}>
                            <div className="form-group">
                                <label>Role ID</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.roleId}
                                    disabled
                                />
                            </div>
                            <div className="form-group">
                                <label>Role Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.roleName}
                                    onChange={(e) => setFormData({...formData, roleName: e.target.value})}
                                    required
                                />
                            </div>
                            <div className="form-group">
                                <label>Display Name *</label>
                                <input
                                    type="text"
                                    className="form-input"
                                    value={formData.displayName}
                                    onChange={(e) => setFormData({...formData, displayName: e.target.value})}
                                    required
                                />
                            </div>
                            
                            <div className="permission-section">
                                <h3>Global Actions (Fallback)</h3>
                                <p className="help-text">These actions apply to all cards unless card-specific actions are defined</p>
                                <div className="action-checkboxes">
                                    {['view', 'create', 'edit', 'delete', 'approve'].map(action => (
                                        <label key={action} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={formData.permissions.actions.includes(action)}
                                                onChange={() => toggleAction(action)}
                                            />
                                            <span>{action}</span>
                                        </label>
                                    ))}
                                </div>
                            </div>

                            <div className="permission-section">
                                <h3>Card Access & Actions</h3>
                                <p className="help-text">Select cards and optionally define specific actions per card</p>
                                <div className="cards-list">
                                    {availableCards.map(card => (
                                        <div key={card.id} className="card-permission-item">
                                            <label className="card-checkbox">
                                                <input
                                                    type="checkbox"
                                                    checked={formData.permissions.cards.includes(card.id)}
                                                    onChange={() => toggleCard(card.id)}
                                                />
                                                <strong>{card.icon} {card.title}</strong>
                                            </label>
                                            
                                            {formData.permissions.cards.includes(card.id) && (
                                                <div className="card-actions">
                                                    <span className="card-actions-label">Specific actions:</span>
                                                    {['view', 'create', 'edit', 'delete', 'approve'].map(action => (
                                                        <label key={action} className="checkbox-label-small">
                                                            <input
                                                                type="checkbox"
                                                                checked={(formData.permissions.cardActions[card.id] || []).includes(action)}
                                                                onChange={() => toggleCardAction(card.id, action)}
                                                            />
                                                            {action}
                                                        </label>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>
                                    Cancel
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    Update Role
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RoleManagement;
